import type { NotificationPageDTO, NotificationPreferencesDTO } from '@giftgroup/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post, put } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';

export const notificationKeys = {
  inbox: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  preferences: ['notification-preferences'] as const,
};

export function useNotifications() {
  return useQuery({
    queryKey: notificationKeys.inbox,
    queryFn: ({ signal }) => get<NotificationPageDTO>('/notifications', signal),
  });
}

/**
 * The bell's badge (doc 11 › UX): refreshed on focus and every minute while the tab is
 * visible. There's no websocket in v1, and a minute is soon enough for gift news.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: ({ signal }) => get<{ count: number }>('/notifications/unread-count', signal),
    refetchInterval: () => (document.visibilityState === 'visible' ? 60_000 : false),
    refetchOnWindowFocus: true,
  });
}

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[] } | { all: true }) =>
      post<void>('/notifications/read', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: notificationKeys.inbox });
      void client.invalidateQueries({ queryKey: notificationKeys.unread });
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: ({ signal }) =>
      get<NotificationPreferencesDTO>('/me/notification-preferences', signal),
  });
}

export function useSavePreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationPreferencesDTO) =>
      put<NotificationPreferencesDTO>('/me/notification-preferences', input),
    onSuccess: (data) => client.setQueryData(notificationKeys.preferences, data),
  });
}

/** Registering this browser's push endpoint with the API (doc 11). */
export const savePushSubscription = (subscription: PushSubscriptionJSON) =>
  post<void>('/push-subscriptions', {
    endpoint: subscription.endpoint,
    keys: { p256dh: subscription.keys?.p256dh, auth: subscription.keys?.auth },
  });

/** A DELETE that carries the endpoint, since that's what identifies the device. */
export const removePushSubscription = (endpoint: string) =>
  api<void>('/push-subscriptions', { method: 'DELETE', body: { endpoint } });
